
import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schemaJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${siteUrl}/contact`,
  url: `${siteUrl}/contact`,
  name: 'お問い合わせ | 株式会社鈴電エンジニア',
  description: '株式会社鈴電エンジニアへのお問い合わせはこちらから。電気設備工事・空調・消防設備工事に関するご相談をお気軽にどうぞ。',
  inLanguage: 'ja',
  isPartOf: { '@id': `${siteUrl}/#website` },
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // SEO meta
    document.title = 'お問い合わせ | 株式会社鈴電エンジニア';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアへのお問い合わせはこちらから。電気設備工事・空調・消防設備工事に関するご相談をお気軽にどうぞ。');

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.text = JSON.stringify(schemaJsonLd);
    document.head.appendChild(script);

    return () => {
      document.title = '株式会社鈴電エンジニア';
      if (desc) desc.setAttribute('content', '株式会社鈴電エンジニアは、電気設備工事を中心に、空調・消防設備工事など、建物に関わる各種設備工事を行っています。');
      const el = document.getElementById('schema-contact');
      if (el) el.remove();
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d69a1dua728k8ctu2l20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow bg-gray-50 pt-24 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">お問い合わせ</h1>
            <p className="text-lg text-gray-600 mb-6">
              ご質問やご相談がございましたら、<br />お気軽にお問い合わせください。
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-center gap-3 text-gray-800">
                <div className="text-center">
                  <p className="font-bold text-lg mb-1">お電話の場合はこちらまでどうぞ。</p>
                  <p className="text-base">
                    <span className="font-bold text-blue-700">TEL：024-583-4612</span>
                  </p>
                  <p className="text-sm text-gray-600 mt-1">8:00～17:00（土日・祝日を除く）</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <div className="mb-12 pb-8 border-b border-gray-200 bg-gray-50 -mx-8 md:-mx-12 px-8 md:px-12 py-6 rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">プライバシーポリシー</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>当サイトは、お客様の個人情報について、お客様の承諾が無い限り第三者に開示、提供を一切いたしません。</p>
                <p>お客様から個人情報をご提供していただき、お客様へのサービスにご利用させていただく場合があります。その目的以外には利用いたしません。</p>
                <p>そして、ご提供いただいた個人情報を取り扱うにあたり管理責任者を置き、適切な管理を行っております。</p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">お問い合わせフォーム</h2>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={8}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                  placeholder="お問い合わせ内容をご記入ください"
                />
                <p className="text-gray-500 text-sm mt-2 text-right">
                  {formData.message.length}/500文字
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700">お問い合わせを受け付けました。担当者より折り返しご連絡いたします。</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">送信に失敗しました。もう一度お試しください。</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-500 text-white py-4 rounded-lg font-bold text-lg hover:bg-blue-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
